import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PageShell from "@/components/brand/PageShell";
import GoogleButton from "@/components/brand/GoogleButton";
import { api } from "@/lib/api";
import { useAuth, dashboardPathForRole } from "@/context/AuthContext";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const { signIn } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            const res = await api.login(email, password);
            signIn(res.user);
            const target = location.state?.from || dashboardPathForRole(res.user.role);
            navigate(target, { replace: true });
        } catch (err) {
            setError(err.message || "Unable to sign in.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <PageShell>
            <section className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
                <div className="st-surface p-8">
                    <h1
                        className="font-serif text-3xl"
                        style={{ color: "var(--st-navy)" }}
                        data-testid="login-heading"
                    >
                        Welcome back
                    </h1>
                    <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        Sign in to continue your curriculum.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
                        <div>
                            <label htmlFor="login-email" className="st-label">Email</label>
                            <input
                                id="login-email"
                                type="email"
                                required
                                autoComplete="email"
                                className="st-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <label htmlFor="login-password" className="st-label">Password</label>
                                <Link
                                    to="/forgot-password"
                                    className="text-xs link-royal"
                                    data-testid="login-forgot-link"
                                >
                                    Forgot?
                                </Link>
                            </div>
                            <input
                                id="login-password"
                                type="password"
                                required
                                autoComplete="current-password"
                                className="st-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                            />
                        </div>

                        {error && (
                            <div
                                className="text-sm px-3 py-2 rounded"
                                style={{ background: "#FEF1F2", color: "#B42318", border: "1px solid #FECDCA" }}
                                data-testid="login-error"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-royal w-full"
                            data-testid="login-submit-btn"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    <div className="my-6 flex items-center gap-3 text-xs" style={{ color: "var(--st-silver-500)" }}>
                        <div className="flex-1 h-px" style={{ background: "var(--st-line)" }} />
                        <span className="tracking-caps">or</span>
                        <div className="flex-1 h-px" style={{ background: "var(--st-line)" }} />
                    </div>

                    <GoogleButton role="student" label="Continue with Google" />

                    <p className="mt-6 text-sm text-center" style={{ color: "var(--st-ink-muted)" }}>
                        New to SwingTrust?{" "}
                        <Link to="/signup" className="link-royal" data-testid="login-signup-link">
                            Enroll as a student
                        </Link>
                        {" · "}
                        <Link to="/affiliate/signup" className="link-royal" data-testid="login-affiliate-link">
                            Affiliate signup
                        </Link>
                    </p>
                </div>
            </section>
        </PageShell>
    );
}
